import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  writeBatch,
  addDoc,
  serverTimestamp,
  orderBy
} from 'firebase/firestore'
import Database from '../types/Database'

/** Accreditation-related clause IDs (those that carry a submissionNumber) */
export const ACCREDITATION_CLAUSE_IDS: Database.AcknowledgementClauseId[] = [
  'indemnity_s4_2',
  'liability_disclaimer_s4_1',
  'user_conduct_s5'
]

export interface AcknowledgementRecord {
  id: string
  userId: string
  email: string
  clauseId: Database.AcknowledgementClauseId
  submissionNumber?: number
  timestamp: Date
}

/**
 * Calculates the next submission number for a user by counting
 * distinct existing submission numbers in their acknowledgement records.
 */
export async function calculateNextSubmissionNumber(
  userId: string
): Promise<number> {
  const db = getFirestore()
  const ackRef = collection(db, 'acknowledgements')
  const q = query(
    ackRef,
    where('userId', '==', userId),
    where('clauseId', 'in', ACCREDITATION_CLAUSE_IDS)
  )
  const snapshot = await getDocs(q)

  const distinctNumbers = new Set<number>()
  snapshot.forEach(docSnap => {
    const data = docSnap.data()
    if (typeof data.submissionNumber === 'number') {
      distinctNumbers.add(data.submissionNumber)
    }
  })

  return distinctNumbers.size + 1
}

/**
 * Writes three acknowledgement records (one per accreditation clause)
 * with the given submission number. Uses writeBatch for atomic writes.
 * Always creates new documents (additive-only).
 */
export async function writeAccreditationAcknowledgements(
  userId: string,
  email: string,
  submissionNumber: number
): Promise<void> {
  const db = getFirestore()
  const batch = writeBatch(db)
  const ackRef = collection(db, 'acknowledgements')

  ACCREDITATION_CLAUSE_IDS.forEach(clauseId => {
    const docRef = doc(ackRef) // auto-generated ID
    batch.set(docRef, {
      userId,
      email,
      clauseId,
      submissionNumber,
      timestamp: serverTimestamp()
    })
  })

  await batch.commit()
}

/**
 * Writes a signup acknowledgement record. No submissionNumber field.
 */
export async function writeSignupAcknowledgement(
  userId: string,
  email: string
): Promise<void> {
  const db = getFirestore()
  const ackRef = collection(db, 'acknowledgements')

  await addDoc(ackRef, {
    userId,
    email,
    clauseId: 'terms_and_conditions_signup',
    timestamp: serverTimestamp()
  })
}

/**
 * Fetches all acknowledgement records for a given user,
 * ordered by timestamp descending (most recent first).
 */
export async function getAcknowledgementHistory(
  userId: string
): Promise<AcknowledgementRecord[]> {
  const db = getFirestore()
  const ackRef = collection(db, 'acknowledgements')
  const q = query(
    ackRef,
    where('userId', '==', userId),
    orderBy('timestamp', 'desc')
  )
  const snapshot = await getDocs(q)

  return snapshot.docs.map(docSnap => {
    const data = docSnap.data()
    return {
      id: docSnap.id,
      userId: data.userId,
      email: data.email,
      clauseId: data.clauseId,
      submissionNumber: data.submissionNumber ?? undefined,
      timestamp: data.timestamp?.toDate() ?? new Date()
    }
  })
}
